/**
 * Νέα εμφάνιση — «Δόθηκαν / Ρέστα» με αριθμητικό, για τη στήλη μετρητών του παραθύρου
 * «Ολοκλήρωση» του Take Away (απόφαση ιδιοκτήτη 15/10/2026: «κάτι σαν το παλιό, να ταιριάζει με
 * το UI μας»). Ίδια λογική με το παράθυρο ρέστων (CashChangeSheet): ως 2 δεκαδικά, «Ακριβές»,
 * +5/+10/+20/+50 που ΠΡΟΣΘΕΤΟΥΝ, «Καθαρισμός». Το ποσό είναι string όπως το cashReceived
 * ('' = δεν δόθηκε ποσό → πληρωμή χωρίς ρέστα). Μόνο εμφάνιση: την πληρωμή την κάνει ο γονέας.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Delete } from 'lucide-react';
import { formatEuro } from '../../utils/formatEuro';

interface CashKeypadProps {
  total: number;
  value: string;
  onChange: (next: string) => void;
}

const QUICK = [5, 10, 20, 50];

/** Το ποσό μετά από ένα πλήκτρο (ίδιο με το CashChangeSheet). */
export function pressCashKey(value: string, key: string): string {
  if (key === 'back') return value.slice(0, -1);
  if (key === '.') return value.includes('.') ? value : (value || '0') + '.';
  const [, decimals] = value.split('.');
  if (decimals !== undefined && decimals.length >= 2) return value;
  return value === '0' ? key : value + key;
}

/** Ρέστα ή «υπολείπονται» για ένα ποσό· null όταν δεν δόθηκε ποσό. */
export function cashOutcome(total: number, value: string): { change: number | null; missing: number | null } {
  if (value === '') return { change: null, missing: null };
  const given = Number(value);
  if (!Number.isFinite(given)) return { change: null, missing: null };
  if (given + 0.001 >= total) return { change: Math.round((given - total) * 100) / 100, missing: null };
  return { change: null, missing: Math.round((total - given) * 100) / 100 };
}

const CashKeypad: React.FC<CashKeypadProps> = ({ total, value, onChange }) => {
  const { t } = useTranslation(['takeaway', 'common']);
  const given = value === '' ? null : Number(value);
  const { change, missing } = cashOutcome(total, value);
  const key = 'h-12 rounded-xl text-xl font-semibold text-t1 bg-surface-1 border border-line hover:bg-surface-3 active:scale-95 transition-all';
  const quick = 'h-11 rounded-xl text-base font-semibold border border-line bg-surface-1 text-t1 hover:bg-surface-3 transition-all active:scale-95';

  return (
    <div className="space-y-3" data-testid="checkout-cash-keypad">
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-xl bg-surface-1 border border-line px-3 py-2.5 tabular-nums">
          <div className="text-sm text-t3">{t('v2.given')}</div>
          <div className="text-2xl font-bold text-t1 leading-tight" data-testid="cash-given">{given === null ? '—' : formatEuro(given)}</div>
        </div>
        <div className={`rounded-xl px-3 py-2.5 tabular-nums ${missing !== null ? 'bg-amber-50 dark:bg-amber-400/10' : 'bg-brand-soft'}`} data-testid="cash-change">
          <div className={`text-sm ${missing !== null ? 'text-amber-800 dark:text-amber-300' : 'text-brand'}`}>
            {missing !== null ? t('v2.missing') : t('v2.change')}
          </div>
          <div className={`text-2xl font-bold leading-tight ${missing !== null ? 'text-amber-800 dark:text-amber-300' : 'text-t1'}`}>
            {missing !== null ? formatEuro(missing) : change !== null ? formatEuro(change) : '—'}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0'].map(k => (
          <button key={k} type="button" onClick={() => onChange(pressCashKey(value, k))} className={key}>{k === '.' ? ',' : k}</button>
        ))}
        <button type="button" onClick={() => onChange(pressCashKey(value, 'back'))} aria-label={t('v2.backspace')} className={`${key} flex items-center justify-center`}>
          <Delete className="h-6 w-6" />
        </button>
      </div>
      <div className="grid grid-cols-4 gap-2">
        {QUICK.map(amount => (
          <button key={amount} type="button" onClick={() => onChange((Number(value || '0') + amount).toFixed(2))} className={quick}>
            +{amount} €
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button type="button" onClick={() => onChange(total.toFixed(2))} className={quick}>
          {t('v2.exact')} · {formatEuro(total)}
        </button>
        <button type="button" onClick={() => onChange('')} className="h-11 rounded-xl text-sm font-medium text-t3 hover:bg-surface-3">
          {t('v2.clearAmount')}
        </button>
      </div>
    </div>
  );
};

export default CashKeypad;
